'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import * as React from 'react';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import { DropdownContext } from "../useDropdown/DropdownContext.js";
import { DropdownActionTypes } from "../useDropdown/useDropdown.types.js";
import { useButton } from "../useButton/useButton.js";
import { combineHooksSlotProps } from "../utils/combineHooksSlotProps.js";
import { extractEventHandlers } from "../utils/index.js";
/**
 *
 * Demos:
 *
 * - [Menu](https://mui.com/base-ui/react-menu/#hooks)
 *
 * API:
 *
 * - [useMenuButton API](https://mui.com/base-ui/react-menu/hooks-api/#use-menu-button)
 */ export function useMenuButton() {
    var parameters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    var _parameters_disabled = parameters.disabled, disabled = _parameters_disabled === void 0 ? false : _parameters_disabled, focusableWhenDisabled = parameters.focusableWhenDisabled, externalRef = parameters.rootRef;
    var menuContext = React.useContext(DropdownContext);
    if (menuContext === null) {
        throw new Error('useMenuButton: no menu context available.');
    }
    var state = menuContext.state, dispatch = menuContext.dispatch, registerTrigger = menuContext.registerTrigger, popupId = menuContext.popupId;
    var _useButton = useButton({
        disabled: disabled,
        focusableWhenDisabled: focusableWhenDisabled,
        rootRef: externalRef
    }), getButtonRootProps = _useButton.getRootProps, buttonRootRef = _useButton.rootRef, active = _useButton.active;
    var handleRef = useForkRef(buttonRootRef, registerTrigger);
    var createHandleClick = function(otherHandlers) {
        return function(event) {
            var _otherHandlers_onClick;
            (_otherHandlers_onClick = otherHandlers.onClick) === null || _otherHandlers_onClick === void 0 ? void 0 : _otherHandlers_onClick.call(otherHandlers, event);
            if (event.defaultMuiPrevented) {
                return;
            }
            dispatch({
                type: DropdownActionTypes.toggle,
                event: event
            });
        };
    };
    var createHandleKeyDown = function(otherHandlers) {
        return function(event) {
            var _otherHandlers_onKeyDown;
            (_otherHandlers_onKeyDown = otherHandlers.onKeyDown) === null || _otherHandlers_onKeyDown === void 0 ? void 0 : _otherHandlers_onKeyDown.call(otherHandlers, event);
            if (event.defaultMuiPrevented) {
                return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                dispatch({
                    type: DropdownActionTypes.open,
                    event: event
                });
            }
        };
    };
    var getOwnRootProps = function() {
        var otherHandlers = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        return {
            onClick: createHandleClick(otherHandlers),
            onKeyDown: createHandleKeyDown(otherHandlers)
        };
    };
    var getRootProps = function() {
        var externalProps = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        var externalEventHandlers = extractEventHandlers(externalProps);
        var getCombinedProps = combineHooksSlotProps(getOwnRootProps, getButtonRootProps);
        return _object_spread_props(_object_spread({
            'aria-haspopup': 'menu',
            'aria-expanded': state.open,
            'aria-controls': popupId
        }, externalProps, externalEventHandlers, getCombinedProps(externalEventHandlers)), {
            tabIndex: 0,
            // this is needed to make the button focused after click in Safari
            ref: handleRef
        });
    };
    return {
        active: active,
        getRootProps: getRootProps,
        open: state.open,
        rootRef: handleRef
    };
}
